import { BarChart3, Users, CheckCircle, FileText, DollarSign } from "lucide-react";
import SalepageRevenueChart from "../../../components/SalepageRevenueChart";

interface SalepageReportsProps {
  stats: any;
  formatCurrency: (amount: number) => string;
}

export default function SalepageReports({ stats, formatCurrency }: SalepageReportsProps) {
  return (
    <div className="space-y-6">
      <div className="grid md:grid-cols-4 gap-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center">
              <Users className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm text-gray-500">ผู้ใช้ทั้งหมด</p>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.totalUsers}</p>
          <p className="text-xs text-green-600 mt-1">+{stats.newUsersThisMonth} เดือนนี้</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-500 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm text-gray-500">Active Subscriptions</p>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.activeSubscriptions}</p>
          <p className="text-xs text-gray-500 mt-1">{((stats.activeSubscriptions / stats.totalUsers) * 100).toFixed(1)}% ของทั้งหมด</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
              <FileText className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm text-gray-500">Salepages</p>
          </div>
          <p className="text-3xl font-bold text-gray-900">{stats.totalSalepages}</p>
          <p className="text-xs text-gray-500 mt-1">{(stats.totalSalepages / stats.totalUsers).toFixed(1)} ต่อผู้ใช้</p>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center">
              <DollarSign className="w-5 h-5 text-white" />
            </div>
            <p className="text-sm text-gray-500">รายได้รวม</p>
          </div>
          <p className="text-2xl font-bold text-gray-900">{formatCurrency(stats.totalRevenue)}</p>
          <p className="text-xs text-green-600 mt-1">+{stats.growthRate.toFixed(1)}%</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl shadow-lg p-8 border border-gray-100">
        <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
          <BarChart3 className="w-6 h-6 text-emerald-600" />
          กราฟรายได้ 6 เดือนย้อนหลัง
        </h2>
        <SalepageRevenueChart />
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-4">สถิติ Subscription</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <span className="text-sm text-gray-700">Active</span>
              <span className="font-bold text-emerald-700">{stats.activeSubscriptions}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-yellow-50 rounded-lg">
              <span className="text-sm text-gray-700">Trial Pending</span>
              <span className="font-bold text-yellow-700">{stats.pendingTrials}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-rose-50 rounded-lg">
              <span className="text-sm text-gray-700">Churn Rate</span>
              <span className="font-bold text-rose-700">{stats.churnRate.toFixed(1)}%</span>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-lg p-6 border border-gray-100">
          <h3 className="text-lg font-bold text-gray-900 mb-4">สถิติรายได้</h3>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg">
              <span className="text-sm text-gray-700">MRR</span>
              <span className="font-bold text-blue-700">{formatCurrency(stats.mrr)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-purple-50 rounded-lg">
              <span className="text-sm text-gray-700">ARPU</span>
              <span className="font-bold text-purple-700">{formatCurrency(stats.arpu)}</span>
            </div>
            <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-lg">
              <span className="text-sm text-gray-700">รายได้เดือนนี้</span>
              <span className="font-bold text-emerald-700">{formatCurrency(stats.revenueThisMonth)}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
